/*  StoreViewsWidget – daily store-view counts  */
import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import {Box, Typography} from '@material-ui/core';
import {Skeleton} from '@material-ui/lab';
import {useSelector} from 'react-redux';
import dayjs from 'dayjs';

import {RootState} from 'appredux/reducers';
import useSupplierAnalytics from '../useSupplierAnalytics';

import './StoreViewsWidget.scss';

/* ───────── helpers ───────── */
const rangeLabel = (f: Date, t: Date) => {
  const df = dayjs(f);
  const dt = dayjs(t);
  if (df.isSame(dt, 'day')) return df.format('DD MMM YYYY');
  if (df.year() === dt.year())
    return `${df.format('DD MMM')} – ${dt.format('DD MMM YYYY')}`;
  return `${df.format('DD MMM YYYY')} – ${dt.format('DD MMM YYYY')}`;
};

const SVTooltip = ({active, payload, label}: any) =>
  !active || !payload?.length ? null : (
    <div className="sv-tooltip">
      <strong>{dayjs(label).format('ddd, DD MMM YYYY')}</strong>
      <br />
      {payload[0].value.toLocaleString()} views
    </div>
  );

/* ───────── component ───────── */
export default function StoreViewsWidget(): React.ReactElement {
  const {start, end} = useSelector((s: RootState) => s.rdwDateFilter);
  const brandId = Number(useSelector((s: RootState) => s.user?.brand?.id ?? 0));
  const {loading, analytics} = useSupplierAnalytics(brandId, start, end);

  /* skeleton */
  if (loading) {
    return <Skeleton variant="rect" height={160} style={{borderRadius: 8}} />;
  }

  if (!analytics) {
    return (
      <Box className="store-views-widget">
        <Typography className="widget-heading">STORE VIEWS</Typography>
        <Typography variant="body2" color="textSecondary">
          Could not load store views.
        </Typography>
      </Box>
    );
  }

  /* ── normalise payload (NEW & legacy) ── */
  const raw =
    analytics.storeViews ??
    (analytics.userViewData ?? []).map((d) => ({
      date: d.date,
      count: d.numberOfViews,
    }));

  /** ⚠️ Some older payloads use `storeViews` instead of `count` */
  const norm = raw.map((d: any) => ({
    date: d.date,
    views: d.count ?? d.storeViews ?? 0, // ← **key fix**
  }));

  /* light down-sample (≤ 90 pts) */
  const step = Math.max(1, Math.ceil(norm.length / 90));
  const data = norm.filter((_, i) => i % step === 0);

  if (data.every((r) => r.views === 0)) {
    return (
      <Box className="store-views-widget">
        <Typography className="widget-heading">STORE VIEWS</Typography>
        <Typography variant="body2" color="textSecondary">
          No data for this period
        </Typography>
      </Box>
    );
  }

  /* render */
  return (
    <Box className="store-views-widget">
      <Box className="widget-head">
        <Typography className="widget-heading">STORE VIEWS</Typography>
        <Typography className="widget-sub" variant="caption">
          {rangeLabel(start, end)}
        </Typography>
      </Box>

      <ResponsiveContainer width="95%" height={160}>
        <LineChart data={data} margin={{top: 10, right: 0, left: 0, bottom: 0}}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={(d) => dayjs(d).format('DD MMM')}
            angle={-15}
            interval="preserveStartEnd"
            height={40}
            tick={{fontSize: 12, fontWeight: 400, color: '#79716B'}}
          />
          <YAxis
            domain={[
              (min: number) => Math.max(0, min - 1),
              (max: number) => max + 1,
            ]}
            allowDecimals={false}
            tick={{fontSize: 12, fontWeight: 400, color: '#79716B'}}
            width={28}
          />
          <Tooltip content={<SVTooltip />} />
          <Line
            type="monotone"
            dataKey="views"
            stroke="#267373"
            strokeWidth={1.75}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}
